/**
 * Staalkiezer — stalen shoppen vanaf een collectiepagina.
 *
 * Op de stalen-collectiepagina staan de HOOFDproducten (zonder prijs). Elke kaart heeft een
 * knop "Staal toevoegen" die het bijbehorende staalproduct toevoegt (product-metaveld
 * intern.staalproduct).
 *
 * Er zijn TWEE modi, afhankelijk van of het paneel <staal-kiezer> op de pagina staat:
 *
 * 1. WINKELMAND-MODUS (standaard, geen paneel). De knop zet het staal direct in de
 *    winkelmand en de normale cart drawer klapt open, net als bij "Gratis staal aanvragen"
 *    op de productpagina. Nog een klik haalt het staal weer uit de winkelmand. De
 *    winkelmand is dan de enige plek waar stalen leven, en Shopify rekent de korting
 *    "3 gratis per categorie" zelf toe.
 *
 * 2. PANEEL-MODUS (het paneel "Jouw stalen" staat op de pagina). De selectie leeft lokaal
 *    in localStorage en gaat pas bij Bestellen in één keer naar de winkelmand. Dit was het
 *    oorspronkelijke ontwerp; de sectie staat nog in het thema, maar niet in de template.
 *    Sleep de sectie "Jouw stalen" erbij en deze modus werkt weer.
 */

import { getCart } from '@theme/cart-state';

const SLEUTEL = 'hds-stalen';
const EVENT = 'stalen:gewijzigd';

/**
 * Variant-ids van de stalen die in de winkelmand zitten. We houden dit lokaal bij zodat
 * één klik ook één winkelmand-request is: het antwoord van add.js en change.js vertelt al
 * wat we nodig hebben. Alleen bij een wijziging van buiten (drawer, andere sectie) lezen
 * we de winkelmand opnieuw.
 */
const gekozenIds = new Set();

/** Staat het paneel op de pagina, dan houden we de selectie lokaal. */
function heeftPaneel() {
  return !!document.querySelector('staal-kiezer');
}

function leesSelectie() {
  try {
    const ruw = JSON.parse(localStorage.getItem(SLEUTEL));
    return Array.isArray(ruw) ? ruw : [];
  } catch {
    return [];
  }
}

function schrijfSelectie(items) {
  try {
    localStorage.setItem(SLEUTEL, JSON.stringify(items));
  } catch {
    /* private mode: de selectie leeft dan alleen in deze pagina */
  }
  document.dispatchEvent(new CustomEvent(EVENT, { detail: { items } }));
}

function bedrag(centen, valuta) {
  return new Intl.NumberFormat('nl-NL', { style: 'currency', currency: valuta || 'EUR' }).format(centen / 100);
}

/* ---------------------------------------------------------------- winkelmand */

/**
 * Section-ids die met de cart-mutatie mee moeten, zodat de drawer verse HTML krijgt.
 * Bewust zonder @theme/cart-section-ids: die staat niet in de import map.
 */
function cartSectieIds() {
  const ids = [];
  document.querySelectorAll('cart-items-component[data-section-id]').forEach((el) => {
    const id = el.dataset.sectionId;
    if (id && !ids.includes(id)) ids.push(id);
  });
  return ids;
}

/**
 * CartAddEvent laat de cart drawer openklappen; cart-drawer.js negeert een gewone
 * CustomEvent met dezelfde naam. Lukt de import niet, dan werkt de winkelmand nog wel,
 * alleen klapt de drawer niet open.
 */
async function meldCartMutatie(detail, isToevoeging) {
  try {
    const { CartAddEvent, CartUpdateEvent } = await import('@theme/events');
    const Klasse = isToevoeging ? CartAddEvent : CartUpdateEvent;
    if (Klasse) {
      document.dispatchEvent(new Klasse({}, 'staalkiezer', { source: 'staalkiezer', ...detail }));
      return;
    }
  } catch {
    /* val terug op een gewoon event */
  }

  document.dispatchEvent(
    new CustomEvent('cart:update', {
      bubbles: true,
      detail: { sourceId: 'staalkiezer', data: { source: 'staalkiezer', ...detail } },
    })
  );
}

/**
 * Meet-events naar de Shopify Web Pixels (Custom Pixel → GTM → GA4/BigQuery), met het
 * hds:-prefix van de andere theme-events (hds:user_engagement, hds:item_list_increment).
 * Het standaard add_to_cart-event bevat alleen het stáálproduct; hiermee ligt ook vast
 * bij wélk hoofdproduct de klant klikte. Faalt stil: meten mag winkelen nooit breken.
 */
function publiceerMeetEvent(naam, payload) {
  try {
    window.Shopify?.analytics?.publish?.(naam, payload);
  } catch {
    /* geen analytics beschikbaar */
  }
}

function meldAnalytics(naam, knop) {
  publiceerMeetEvent(naam, {
    staal_variant_id: String(knop.dataset.staalId),
    hoofdproduct_titel: knop.dataset.staalTitel || '',
    hoofdproduct_url: knop.dataset.staalUrl || '',
    staal_prijs_centen: Number(knop.dataset.staalPrijs || 0),
    bron: knop.dataset.staalBron || 'collectie-kaart',
  });
}

/**
 * Eén event per paginaweergave zodra er staal-knoppen staan. Zo zijn ook sessies die de
 * knoppen alleen ZÁGEN herkenbaar in GA4/BigQuery — de controle-variant heeft deze code
 * niet en meldt dus niets. Zonder dit event zou je alleen klikkers kunnen onderscheiden.
 */
let exposureGemeld = false;

function meldExposure() {
  if (exposureGemeld) return;
  const aantal = document.querySelectorAll('[data-staal-knop]').length;
  if (!aantal) return;
  exposureGemeld = true;
  publiceerMeetEvent('hds:staal_exposure', { aantal_knoppen: aantal, bron: 'collectie' });
}

/**
 * De drawer luistert op CartAddEvent en checkt `event instanceof CartAddEvent`. Die check
 * faalt zodra de drawer een andere module-instantie van @theme/events gebruikt dan wij
 * (andere asset-hash in de import map). Daarom openen we hem ook rechtstreeks via zijn
 * eigen API. De attribuutcheck respecteert de thema-instelling: staat auto-open uit, dan
 * forceren we niets.
 */
async function openCartDrawer() {
  const drawer = document.querySelector('cart-drawer-component[auto-open]');
  if (!drawer) return;

  const isOpen = () => drawer.querySelector('dialog')?.open === true;

  const probeer = async () => {
    try {
      if (typeof drawer.open === 'function') await drawer.open();
      else if (typeof drawer.showDialog === 'function') drawer.showDialog();
    } catch (fout) {
      console.error('[staalkiezer] cart drawer openen mislukt', fout);
    }
  };

  // Eén poging, en als de sectie-render de dialog er tussenuit haalt nog één. Niet meer:
  // drawer.open() kan zelf de drawer-HTML ophalen, dus doorpollen kost requests.
  await probeer();
  if (isOpen()) return;

  await new Promise((klaar) => setTimeout(klaar, 400));
  if (!isOpen()) await probeer();
}

async function leesWinkelmand({ vers = false } = {}) {
  try {
    return await getCart({ vers });
  } catch {
    return null;
  }
}

/** Variant-ids van de stalen die nu in de winkelmand zitten. */
function stalenInWinkelmand(cart) {
  if (!cart?.items) return new Set();
  return new Set(cart.items.map((item) => String(item.variant_id)));
}

async function voegStaalToe(knop) {
  // Bewust ZONDER sections: die payload laat de drawer zijn HTML opnieuw renderen, en die
  // render viel samen met het openen waardoor de dialog meteen weer dicht stond. De drawer
  // haalt zijn inhoud zelf op wanneer hij opent.
  // _bron reist als line-item property mee tot in de order (underscore = niet zichtbaar
  // voor de klant), zodat staal-orders uit deze flow ook zonder analytics herkenbaar zijn.
  const body = {
    items: [{ id: Number(knop.dataset.staalId), quantity: 1, properties: { _bron: 'staal-kiezer' } }],
  };

  const antwoord = await fetch('/cart/add.js', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify(body),
  });

  if (!antwoord.ok) throw new Error(`Winkelmand gaf ${antwoord.status}`);

  const data = await antwoord.json();
  gekozenIds.add(String(knop.dataset.staalId));
  markeerKnoppen(gekozenIds);
  meldAnalytics('hds:staal_add', knop);

  await meldCartMutatie({ itemCount: data.item_count, variantId: knop.dataset.staalId }, true);
  await openCartDrawer();
}

async function haalStaalWeg(knop) {
  // change.js accepteert het variant-id, dus we hoeven de winkelmand niet eerst te lezen
  // om de line-key op te zoeken.
  const antwoord = await fetch('/cart/change.js', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify({ id: String(knop.dataset.staalId), quantity: 0 }),
  });

  if (!antwoord.ok) {
    // 400 betekent hier meestal dat de regel er niet meer is, bijvoorbeeld omdat hij
    // elders is weggehaald of omdat twee kaarten hetzelfde staal delen. Geen fout voor de
    // klant: we trekken de knopstaat gelijk met de werkelijke winkelmand.
    await synchroniseerMetWinkelmand();
    return;
  }

  const data = await antwoord.json();
  gekozenIds.delete(String(knop.dataset.staalId));
  markeerKnoppen(gekozenIds);
  meldAnalytics('hds:staal_remove', knop);

  await meldCartMutatie({ itemCount: data.item_count }, false);
}

/* -------------------------------------------------------------------- knoppen */

/**
 * Formaten van een knop die er meerdere heeft (SHO-9835). Leeg voor de gewone knoppen.
 * Op klik-moment uit het dataset lezen, want na een her-render van het raster zijn het
 * andere DOM-nodes.
 */
function formatenVan(knop) {
  if (!knop.dataset.staalVarianten) return [];
  try {
    const lijst = JSON.parse(knop.dataset.staalVarianten);
    return Array.isArray(lijst) ? lijst : [];
  } catch {
    return [];
  }
}

/** Zet alle knoppen in de juiste staat op basis van een set variant-ids. */
function markeerKnoppen(ids) {
  document.querySelectorAll('[data-staal-knop]').forEach((knop) => {
    const formaten = formatenVan(knop);

    // Bij één formaat is "zit het in de mand" het hele verhaal. Bij meerdere niet: dan moet
    // de knop vertellen wélk formaat de klant pakte, anders staat er "Toegevoegd" terwijl
    // de klant niet kan zien of dat het gratis staal of de XL van € 35 was.
    const gekozenNamen = formaten
      .filter((formaat) => ids.has(String(formaat.id)))
      .map((formaat) => formaat.naam);

    const gekozen = formaten.length ? gekozenNamen.length > 0 : ids.has(String(knop.dataset.staalId));

    knop.classList.toggle('is-gekozen', gekozen);
    knop.setAttribute('aria-pressed', gekozen ? 'true' : 'false');

    const label = knop.querySelector('[data-staal-knop-label]');
    if (label) {
      if (!gekozen) {
        label.textContent = knop.dataset.labelToevoegen;
      } else if (gekozenNamen.length) {
        // Het formaat in een eigen span, zodat staal-knop.css het op mobiel kan verbergen:
        // "Toegevoegd · Standaard" is 22 tekens en past niet in een kaart van een 2-koloms
        // raster — de knop werd dan breder dan de kaart. Zie SHO-9896.
        label.textContent = knop.dataset.labelGekozen;
        const formaat = document.createElement('span');
        formaat.className = 'staal-knop__formaat';
        formaat.textContent = ` · ${gekozenNamen.join(', ')}`;
        label.append(formaat);
      } else {
        label.textContent = knop.dataset.labelGekozen;
      }
    }
  });
}

function synchroniseerKnoppen(items) {
  markeerKnoppen(new Set(items.map((item) => String(item.id))));
}

async function synchroniseerMetWinkelmand({ vers = false } = {}) {
  const cart = await leesWinkelmand({ vers });
  if (!cart) return;

  gekozenIds.clear();
  stalenInWinkelmand(cart).forEach((id) => gekozenIds.add(id));
  markeerKnoppen(gekozenIds);
}

/** Klikken op "Staal toevoegen": winkelmand-modus of paneel-modus. */
document.addEventListener('click', async (event) => {
  const knop = event.target.closest('[data-staal-knop]');
  if (!knop) return;

  // Meerdere formaten: dan opent staal-formaatkiezer.js de sheet. Die luistert in de
  // capture-fase en stopt het event daar al, dus normaal komen we hier niet eens. De guard
  // is er voor het geval de registratievolgorde ooit omdraait. Bewust op de DEFINITIE van
  // het element en niet op zijn aanwezigheid: het element staat er altijd (Liquid), het
  // script niet per se. Laadde het script niet, dan valt de knop terug op zijn oude gedrag
  // — een staal in de Standaard-maat is beter dan een dode knop. (SHO-9835)
  if (knop.dataset.staalVarianten && customElements.get('staal-formaatkiezer')) return;

  event.preventDefault();
  event.stopPropagation();

  const id = knop.dataset.staalId;
  if (!id || knop.dataset.bezig === 'true') return;

  if (heeftPaneel()) {
    const items = leesSelectie();
    const bestaand = items.findIndex((item) => String(item.id) === String(id));

    if (bestaand > -1) {
      items.splice(bestaand, 1);
    } else {
      items.push({
        id,
        titel: knop.dataset.staalTitel || '',
        beeld: knop.dataset.staalBeeld || '',
        prijs: Number(knop.dataset.staalPrijs || 0),
        url: knop.dataset.staalUrl || '',
      });
    }

    schrijfSelectie(items);
    return;
  }

  const staatErAl = knop.classList.contains('is-gekozen');
  knop.dataset.bezig = 'true';
  knop.classList.add('is-bezig');

  try {
    if (staatErAl) {
      await haalStaalWeg(knop);
    } else {
      await voegStaalToe(knop);
    }
  } catch (fout) {
    console.error('[staalkiezer] winkelmand bijwerken mislukt', fout);
    // Knopstaat mag niet blijven liegen als de mutatie faalde.
    await synchroniseerMetWinkelmand();
  } finally {
    delete knop.dataset.bezig;
    knop.classList.remove('is-bezig');
  }
});

/* ---------------------------------------------------- paneel "Jouw stalen" */

class StaalKiezer extends HTMLElement {
  connectedCallback() {
    this.gratis = Number(this.getAttribute('gratis') || 3);
    // Aantal lege slots dat we tonen zolang de klant er minder heeft gekozen. Zo leest de
    // lege staat als een uitnodiging én zie je hoeveel er gratis zijn.
    this.legeSlots = Number(this.getAttribute('lege-slots') || this.gratis);
    this.valuta = this.getAttribute('valuta') || 'EUR';

    this.slots = this.querySelector('[data-stalen-slots]');
    this.aantalUit = this.querySelector('[data-stalen-aantal]');
    this.toeslagUit = this.querySelector('[data-stalen-toeslag]');
    this.knop = this.querySelector('[data-stalen-bestellen]');
    this.melding = this.querySelector('[data-stalen-melding]');

    this.onWijziging = (event) => this.render(event.detail.items);
    document.addEventListener(EVENT, this.onWijziging);

    this.addEventListener('click', (event) => {
      const verwijder = event.target.closest('[data-stalen-verwijder]');
      if (verwijder) {
        const items = leesSelectie().filter((item) => String(item.id) !== verwijder.dataset.staalId);
        schrijfSelectie(items);
        return;
      }

      if (event.target.closest('[data-stalen-bestellen]')) this.bestel();
    });

    this.render(leesSelectie());
  }

  disconnectedCallback() {
    document.removeEventListener(EVENT, this.onWijziging);
  }

  render(gekozen) {
    const aantalSlots = Math.max(gekozen.length, this.legeSlots);

    if (this.slots) {
      this.slots.innerHTML = '';
      for (let i = 0; i < aantalSlots; i += 1) {
        this.slots.append(gekozen[i] ? this.gevuldeSlot(gekozen[i], i) : this.legeSlot(i));
      }
    }

    if (this.aantalUit) {
      this.aantalUit.textContent =
        gekozen.length === 1 ? '1 staal gekozen' : `${gekozen.length} stalen gekozen`;
    }

    const betaald = gekozen.slice(this.gratis);
    const toeslag = betaald.reduce((som, item) => som + (item.prijs || 0), 0);

    if (this.toeslagUit) {
      if (toeslag > 0) {
        const woord = betaald.length === 1 ? 'staal' : 'stalen';
        this.toeslagUit.textContent = `${betaald.length} extra ${woord} · ${bedrag(toeslag, this.valuta)}`;
        this.toeslagUit.hidden = false;
      } else {
        this.toeslagUit.hidden = true;
      }
    }

    if (this.knop) {
      this.knop.disabled = gekozen.length === 0;
    }

    this.classList.toggle('is-leeg', gekozen.length === 0);
    synchroniseerKnoppen(gekozen);
  }

  legeSlot(index) {
    const li = document.createElement('li');
    li.className = 'jouw-stalen__slot jouw-stalen__slot--leeg';

    const vlak = document.createElement('span');
    vlak.className = 'jouw-stalen__vlak';
    li.append(vlak);

    if (index >= this.gratis) {
      const hint = document.createElement('span');
      hint.className = 'jouw-stalen__hint';
      hint.textContent = 'extra';
      li.append(hint);
    }

    return li;
  }

  gevuldeSlot(item, index) {
    const li = document.createElement('li');
    li.className = 'jouw-stalen__slot';

    const vlak = document.createElement('span');
    vlak.className = 'jouw-stalen__vlak';

    if (item.beeld) {
      const img = document.createElement('img');
      img.src = item.beeld;
      img.alt = '';
      img.loading = 'lazy';
      img.className = 'jouw-stalen__beeld';
      vlak.append(img);
    }

    const weg = document.createElement('button');
    weg.type = 'button';
    weg.className = 'jouw-stalen__verwijder';
    weg.dataset.stalenVerwijder = '';
    weg.dataset.staalId = item.id;
    weg.setAttribute('aria-label', `${item.titel} uit je selectie halen`);
    weg.innerHTML =
      '<svg viewBox="0 0 16 16" aria-hidden="true" focusable="false"><path d="M5 5l6 6M11 5l-6 6" stroke="currentColor" stroke-width="1.6" stroke-linecap="round"/></svg>';

    vlak.append(weg);
    li.append(vlak);

    const naam = document.createElement('span');
    naam.className = 'jouw-stalen__naam';
    naam.textContent = item.titel;
    li.append(naam);

    if (index >= this.gratis && item.prijs) {
      const prijs = document.createElement('span');
      prijs.className = 'jouw-stalen__hint';
      prijs.textContent = `+ ${bedrag(item.prijs, this.valuta)}`;
      li.append(prijs);
    }

    return li;
  }

  async bestel() {
    const items = leesSelectie();
    if (!items.length) return;

    this.knop.disabled = true;
    this.knop.classList.add('is-bezig');

    try {
      const antwoord = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          items: items.map((item) => ({
            id: Number(item.id),
            quantity: 1,
            properties: { _bron: 'staal-kiezer-paneel' },
          })),
        }),
      });

      if (!antwoord.ok) throw new Error(`Winkelmand gaf ${antwoord.status}`);

      schrijfSelectie([]);
      window.location.href = '/cart';
    } catch (fout) {
      console.error('[staalkiezer] toevoegen aan winkelmand mislukt', fout);
      this.knop.disabled = false;
      this.knop.classList.remove('is-bezig');

      if (this.melding) {
        this.melding.textContent = 'Het toevoegen lukte niet. Probeer het opnieuw.';
        this.melding.hidden = false;
      }
    }
  }
}

if (!customElements.get('staal-kiezer')) {
  customElements.define('staal-kiezer', StaalKiezer);
}

/* ------------------------------------------------------------ initialisatie */

/**
 * Filteren en sorteren renderen het productraster opnieuw via de Section Rendering API.
 * De verse knoppen weten dan niet meer wat er al gekozen is en zouden "Staal toevoegen"
 * tonen voor een staal dat al in de winkelmand zit — een klik haalt het er dan juist af.
 * Daarom hersynchroniseren we zodra er knoppen bijkomen.
 */
function houdKnoppenGesynchroniseerd() {
  const doel =
    document.querySelector('results-list') ||
    document.querySelector('[data-hds-algolia-search-modal]') ||
    document.querySelector('[data-hds-algolia-search]') ||
    document.body;

  const observer = new MutationObserver((mutaties) => {
    const erKwamenKnoppenBij = mutaties.some((mutatie) =>
      Array.from(mutatie.addedNodes).some(
        (node) =>
          node.nodeType === 1 &&
          (node.matches?.('[data-staal-knop]') || node.querySelector?.('[data-staal-knop]'))
      )
    );

    if (!erKwamenKnoppenBij) return;
    // Kwamen de eerste knoppen pas ná een her-render in beeld, dan telt dat ook als exposure.
    meldExposure();
    // Geen cart-fetch: de set is al actueel, alleen de verse knoppen moeten bijgewerkt.
    if (heeftPaneel()) synchroniseerKnoppen(leesSelectie());
    else markeerKnoppen(gekozenIds);
  });

  observer.observe(doel, { childList: true, subtree: true });
}

function start() {
  // Het script hangt aan staal-markup (SHO-10262), niet meer site-breed in scripts.liquid.
  // Zonder knoppen en zonder paneel doen we niets: geen observer en geen cart-fetch.
  const kanKnoppenKrijgen =
    document.querySelector('[data-staal-knop]') ||
    document.querySelector('results-list') ||
    document.querySelector('[data-hds-algolia-search-modal]') ||
    document.querySelector('[data-hds-algolia-search]');
  if (!kanKnoppenKrijgen && !heeftPaneel()) return;

  if (heeftPaneel()) synchroniseerKnoppen(leesSelectie());
  else synchroniseerMetWinkelmand();

  meldExposure();
  houdKnoppenGesynchroniseerd();

  // Wijzigt de winkelmand elders (drawer, andere sectie), dan lopen de knoppen mee.
  // Wijzigt de winkelmand ELDERS (drawer, andere sectie), dan opnieuw uitlezen. Onze eigen
  // events slaan we over: de set is dan al bijgewerkt.
  document.addEventListener('cart:update', (event) => {
    if (heeftPaneel()) return;
    if (event.detail?.sourceId === 'staalkiezer') return;
    synchroniseerMetWinkelmand({ vers: true });
  });
}

document.addEventListener(EVENT, (event) => {
  if (heeftPaneel()) synchroniseerKnoppen(event.detail.items);
});

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', start);
} else {
  start();
}
